import { SimpleGrid } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import React from "react";
import {
  CodeIcon,
  ExperimentsIcon,
  EyeSlashIcon,
  LeversIcon,
  MegaphoneIcon,
  PersonWithCheckIcon,
  ShoppingBagIcon,
  TrendIcon,
} from "ui/icons";
import { FilterTabs } from "ui/tabs";
import { TextColumn, TextTitle } from "ui/text";

export function CampaignUseCases() {
  return (
    <Section my="0" pb={sectionSpacingY}>
      <TextTitle size="lg" align="center" title="Solve real problems, fast" mb={{ base: "bigger", md: "biggest" }} />

      <FilterTabs
        gap={{ base: "bigger", md: "biggest" }}
        tabs={[
          {
            label: "Orchestration",
            items: <OrchestrationTabs />,
          },
          {
            label: "Experimentation",
            items: <ExperimentationTabs />,
          },
        ]}
      />
    </Section>
  );
}

function OrchestrationTabs() {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={{ base: "biggest", md: "small" }}>
      <TextColumn
        iconChip={{ variant: "violet", icon: EyeSlashIcon }}
        title="Reduce wasted spend with better suppression"
        description="Build custom suppression audiences and actively sync them into advertising platforms to optimize performance."
        cta={{ label: "Learn more", href: "/playbooks/Suppress-Existing-Customers-from-Facebook-Campaigns" }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: CodeIcon }}
        title="Increase ROAS with better conversion signals"
        description="Send Conversion API events with rich customer data and conversion values so that the ad platforms can optimize targeting."
        cta={{ label: "Learn more", href: "/playbooks/send-better-signals-with-facebook-conversion-api" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: ShoppingBagIcon }}
        title="Expand LTV with win-back, cross-sell, and upsell"
        description="Maximize your customer lifetime value by winning back churned customers, cross-selling new products, and upselling your existing customers."
        cta={{
          label: "Learn more",
          href: "/playbooks/improve-conversions-with-personalized-funnel-based-ad-retargeting",
        }}
      />
      <TextColumn
        iconChip={{ variant: "red", icon: PersonWithCheckIcon }}
        title="Scale successful campaigns with lookalike audiences"
        description="Drive more revenue from your existing campaigns by seeding quality lookalike audiences based on your existing high value customers."
        cta={{ label: "Learn more", href: "/playbooks/create-lookalike-audiences-from-high-value-users" }}
      />
    </SimpleGrid>
  );
}

function ExperimentationTabs() {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={{ base: "biggest", md: "small" }}>
      <TextColumn
        iconChip={{ variant: "blue", icon: LeversIcon }}
        title="Understand the true lift of every channel with holdout testing"
        description="Understand incrementality of email and advertising programs with audience level hold-out testing."
        cta={{ label: "Learn more", href: "/platform/customer-studio#splits" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: ExperimentsIcon }}
        title="Drive higher ROI with omni-channel experimentation"
        description="Unlock audience-level testing across multiple channels. Easily measure user-level ROI across multiple treatments."
        cta={{ label: "Learn more", href: "/docs/customer-studio/splits", target: "_blank" }}
      />
      <TextColumn
        iconChip={{ variant: "violet", icon: TrendIcon }}
        title="Measure incremental brand lift & tune-in"
        description="Test different audiences and measure the impact on brand awareness and tune-in through integrations with Dynata & iSpot."
        cta={{ label: "Learn more", href: "/blog/hightouch-for-ctv" }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: MegaphoneIcon }}
        title="Move more confidently with complete campaign data"
        description="Understand the results of tests directly in Hightouch with campaign and performance data in one place."
        cta={{ label: "Learn more", href: "/platform/campaign-intelligence" }}
      />
    </SimpleGrid>
  );
}
